/** Monetary presentation metadata; never infer classifications or historical rates. */
export type ProcessingItem = {
  processing_components?: { charge_type: string; billing_quantity: string | number; rate: string | number | null; exact: string | number; amount: string | number; missing: boolean }[];
  processingComponents?: ProcessingItem["processing_components"];
  id?: number; name?: string | null;
  charge_type?: string; chargeType?: string;
  quantity?: number; weight_ton?: string | number; weightTon?: number;
  row_number?: number; rowNumber?: number;
  shape_id?: number | null; shapeId?: number | null;
  shape_name?: string | null; shapeName?: string | null;
  shape_number?: string | null; shapeNumber?: string | null;
  processing?: string | number;
  processing_exact?: string | number; processingExact?: number;
  processing_rate?: string | number | null; processingRate?: number | null;
  billing_quantity?: string | number; billingQuantity?: number;
  processing_missing?: boolean; processingMissing?: boolean;
};

export const preciseNumber = (value: number, minimumFractionDigits = 0) => value.toLocaleString("en-US", {
  minimumFractionDigits, maximumFractionDigits: 12,
  ...(value !== 0 && Math.abs(value) < 1e-12 ? { notation: "scientific" as const } : {}),
});

export function processingRows(items: ProcessingItem[] | undefined) {
  return (items || []).flatMap(item => {
    const components = item.processing_components ?? item.processingComponents;
    return components ? components.map(component => ({ ...item, charge_type: component.charge_type,
      billing_quantity: component.billing_quantity, processing_rate: component.rate,
      processing_exact: component.exact, processing: component.amount, processing_missing: component.missing })) : [item];
  }).filter(item => ["cut", "bend", "unit"].includes(item.charge_type ?? item.chargeType ?? ""))
    .map(item => {
      const charge = item.charge_type ?? item.chargeType!;
      const quantity = item.billing_quantity ?? item.billingQuantity ?? (charge === "unit" ? item.quantity : item.weight_ton ?? item.weightTon);
      const rate = item.processing_rate ?? item.processingRate;
      const exact = item.processing_exact ?? item.processingExact;
      const amount = item.processing;
      return { item, charge, quantity: quantity == null ? null : Number(quantity),
        rate: rate == null ? null : Number(rate), amount: amount == null ? null : Number(amount),
        exact: exact == null ? null : Number(exact), missing: Boolean(item.processing_missing ?? item.processingMissing) };
    });
}

/** Only recorded exact totals are displayed; historical snapshots remain rounded. */
export function processingAmount(totals: Record<string, string | number>, key: "cut" | "bend" | "units") {
  const billed = preciseNumber(Number(totals[key] ?? 0), 2);
  const raw = totals[`${key}_exact`];
  if (raw == null || !Number.isFinite(Number(raw))) return billed;
  const exact = preciseNumber(Number(raw), 2);
  return exact === billed ? billed : `${exact} ≈ ${billed}`;
}
